// src/components/JoinGroupByCode.js
import { useState } from "react";
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, Paper, Stack, TextField, Typography,
} from "@mui/material";
import * as groupsService from "../services/groupsService";
import { useJoinGroupByJoinCode } from "../hooks/useGroups";
import { logger } from "../utils/logger";

const isDuplicateKeyError = (error) =>
  !!error && (error.code === "23505" ||
    /duplicate key value violates unique constraint/i.test(error?.message || ""));

export default function JoinGroupByCode({ userGroupIds, onJoined }) {
  const joinGroupMutation = useJoinGroupByJoinCode();
  const [joinCode, setJoinCode] = useState("");
  const [resolvedGroup, setResolvedGroup] = useState(null);
  const [checkingCode, setCheckingCode] = useState(false);
  const [codeError, setCodeError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const handleContinue = async () => {
    const code = joinCode.trim();
    if (!code) return;

    setCheckingCode(true);
    setCodeError(null);
    try {
      const group = await groupsService.findGroupByJoinCode({ code });
      if (!group) {
        setCodeError("Invalid or expired join code.");
        return;
      }
      if (userGroupIds.includes(group.id)) {
        logger.warn('JoinGroupByCode', 'join_group_already_member', { groupId: group.id, mode: 'pre_check' });
        setCodeError(`You're already a member of "${group.name}".`);
        return;
      }
      setResolvedGroup(group);
      setConfirming(true);
    } catch {
      setCodeError("Something went wrong checking that code. Please try again.");
    } finally {
      setCheckingCode(false);
    }
  };

  const handleConfirm = async () => {
    if (!resolvedGroup) return;
    setSaving(true);
    setError(null);
    try {
      const group = await joinGroupMutation.mutateAsync({ code: joinCode.trim() });
      setConfirming(false);
      setJoinCode("");
      setResolvedGroup(null);
      setSuccessMessage(`Joined ${group.name}!`);
      onJoined?.(group);
    } catch (err) {
      if (isDuplicateKeyError(err)) {
        logger.warn('JoinGroupByCode', 'join_group_already_member', { groupId: resolvedGroup.id, mode: 'race' });
        setError(`You're already a member of "${resolvedGroup.name}".`);
      } else {
        setError("Failed to join group. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleGoBack = () => setConfirming(false);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
        Join an Additional Group
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Have a join code from another group's Leader? Enter it below to join instantly
        — no approval needed. This won't change the group you're currently viewing.
      </Typography>
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="flex-start">
        <TextField
          size="small"
          label="Join code"
          value={joinCode}
          onChange={(e) => { setJoinCode(e.target.value.toUpperCase()); setCodeError(null); }}
          inputProps={{ maxLength: 6, style: { textAlign: "center", letterSpacing: "0.3em" } }}
        />
        <Button
          variant="contained"
          disabled={!joinCode.trim() || checkingCode}
          onClick={handleContinue}
        >
          {checkingCode ? "Checking..." : "Continue"}
        </Button>
      </Stack>
      {codeError && (
        <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
          {codeError}
        </Typography>
      )}

      <Dialog open={confirming} onClose={handleGoBack}>
        <DialogTitle>Join Group</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Join <strong>{resolvedGroup?.name}</strong>? You'll be added right away —
            no approval needed.
          </DialogContentText>
          {error && (
            <Typography variant="caption" color="error" sx={{ display: "block", mt: 2 }}>
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleGoBack} disabled={saving}>Go Back</Button>
          <Button variant="contained" color="success" disabled={saving} onClick={handleConfirm}>
            {saving ? "Joining..." : "Join Group"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
